import axios, { AxiosProgressEvent } from "axios";
import Config from "config";
import { getHeaders } from "helpers/headers";
import { AnyAction } from "interfaces/action";
import { Dispatch } from "interfaces/dispatch";
import { PaginatedQuery, PaginatedResponse } from "interfaces/paginated-query";
import { ThunkActionCreator } from "interfaces/thunk";
import { User } from "interfaces/user";
import queryString from "query-string";
import { handleActions } from "redux-actions";
import { CREATE_PROJECT_REQUEST } from "state/create_project";
import { DELETE_USER } from "state/user";
import {SeverityLevel, ToastService} from "../services/toastService";
import { SORTING_ORDER } from "enum/sorting.enum";

// Actions Types
export const FETCH_USERS_BY_PROJECT_REQUEST = "FETCH_USERS_BY_PROJECT_REQUEST";
export const FETCH_USERS_BY_PROJECT = "FETCH_USERS_BY_PROJECT";
export const FETCH_USERS_BY_PROJECT_FAILED = "FETCH_USERS_BY_PROJECT_FAILED";

export const FETCH_USERS_REQUEST = "FETCH_USERS_REQUEST";
export const FETCH_USERS = "FETCH_USERS";
export const FETCH_USERS_FAILED = "FETCH_USERS_FAILED";

export const FILTER_USERS_REQUEST = "FILTER_USERS_REQUEST";
export const FILTER_USERS = "FILTER_USERS";
export const FILTER_USERS_FAILED = "FILTER_USERS_FAILED";

export const CHECK_USERS_REQUEST = "CHECK_USERS_REQUEST";
export const CHECK_USERS = "CHECK_USERS";
export const CHECK_USERS_FAILED = "CHECK_USERS_FAILED";
export const FETCH_REGIONS_REQUEST = "FETCH_REGIONS_REQUEST"
export const FETCH_REGIONS = "FETCH_REGIONS"
export const FETCH_REGIONS_FAILED = "FETCH_REGIONS_FAILED"

export const SAVE_USER_REQUEST = "SAVE_USER_REQUEST"
export const SAVE_USER = "SAVE_USER"
export const SAVE_USER_FAILED = "SAVE_USER_FAILED"

export const PATCH_USER_REQUEST = "PATCH_USER_REQUEST"
export const PATCH_USER = "PATCH_USER"
export const PATCH_USER_FAILED = "PATCH_USER_FAILED"

// Thunk Action Creator
const fetchUsers: ThunkActionCreator = (page: number = 1, limit: number = 100, sortField?: string, sortOrder?: SORTING_ORDER) => (dispatch: Dispatch) => {
  dispatch({ type: FETCH_USERS_REQUEST });
  const params: Record<string, any> = { page, limit };
  if (sortField) {
    params.sortField = sortField;
    params.sortOrder = sortOrder || SORTING_ORDER.ASC;
  }
  return axios
    .get(`${Config.API_SERVICE_URL}/api/users?${queryString.stringify(params)}`, getHeaders())
    .then((response) =>
      dispatch({
        type: FETCH_USERS,
        payload: { data: response.data.data, pagination: response.data.pagination },
      })
    )
    .catch((error: Error) => dispatch({ type: FETCH_USERS_FAILED }));
};

const fetchUsersByProjectId: ThunkActionCreator =
  (
    projectId: number,
    queryOptions?: PaginatedQuery & { email?: string; search?: string }
  ) =>
  (dispatch: Dispatch) => {
    dispatch({ type: FETCH_USERS_BY_PROJECT_REQUEST });
    const normalizedQueryOptions = queryOptions ?? {
      size: 100000000,
      page: 0,
    };

    const url = `${Config.API_SERVICE_URL}/api/projects/${projectId}/users?${queryString.stringify(
      normalizedQueryOptions
    )}`;

    return axios
      .get(url, getHeaders())
      .then((response) => dispatch({ type: FETCH_USERS_BY_PROJECT, payload: response.data }))
      .catch((error: Error) => dispatch({ type: FETCH_USERS_BY_PROJECT_FAILED }));
  };

const filterUsers: ThunkActionCreator =
  (
    queryParams?: {
      email?: string;
      firstname?: string;
      lastname?: string;
      role?: string;
      company?: string;
      lastLoggedIn?: string;
      createdAt?: string;
      page?: number;
      limit?: number;
      sortField?: string;
      sortOrder?: SORTING_ORDER;
    }
  ) =>
  (dispatch: Dispatch) => {
    dispatch({ type: FILTER_USERS_REQUEST });
    const params: Record<string, any> = {
      page: queryParams?.page || 1,
      limit: queryParams?.limit || 100,
    };
    if (queryParams?.email) params.email = queryParams.email;
    if (queryParams?.firstname) params.firstname = queryParams.firstname;
    if (queryParams?.lastname) params.lastname = queryParams.lastname;
    if (queryParams?.role) params.role = queryParams.role;
    if (queryParams?.company) params.company = queryParams.company;
    if (queryParams?.lastLoggedIn) params.lastLoggedIn = queryParams.lastLoggedIn;
    if (queryParams?.createdAt) params.createdAt = queryParams.createdAt;
    if (queryParams?.sortField) {
      params.sortField = queryParams.sortField;
      params.sortOrder = queryParams.sortOrder || SORTING_ORDER.ASC;
    }
    const url = `${Config.API_SERVICE_URL}/api/users?${queryString.stringify(params)}`;
    return axios
      .get(url, getHeaders())
      .then((response) =>
        dispatch({
          type: FILTER_USERS,
          payload: { data: response.data.data, pagination: response.data.pagination },
        })
      )
      .catch((error: Error) => dispatch({ type: FILTER_USERS_FAILED }));
  };

//   /api/users-stores/checkUsers
// send payload like this {IDs: ["10002", "10002"]}
// response will be like this
//       availableUserIds: existingIds,
//       notAvailableUserIds,

const checkUsers: ThunkActionCreator<Promise<any>> =
  (ids: (number | string)[]) => (dispatch: Dispatch) => {
    dispatch({ type: CHECK_USERS_REQUEST });

    return axios
      .post(
        `${Config.API_SERVICE_URL}/api/users-stores/checkUsers`,
        JSON.stringify({ emailIds: ids }),
        getHeaders({ "Content-Type": "application/json" })
      )
      .then((response) => {
        dispatch({ type: CHECK_USERS, payload: response.data });
        return response.data;
      })
      .catch((error) => {
        dispatch({ type: CHECK_USERS_FAILED });
        throw error;
      });
  };
  const getRegions: ThunkActionCreator = () => (dispatch: Dispatch) => {
    dispatch({ type: FETCH_REGIONS_REQUEST });
    return axios
      .get(`${Config.API_SERVICE_URL}/api/getRegions`, getHeaders())
      .then((response) => dispatch({ type: FETCH_REGIONS, payload:  response.data}))
      .catch((error: Error) => dispatch({ type: FETCH_REGIONS_FAILED }));
  }

const saveUser: ThunkActionCreator<Promise<void>> = (userDetails) => (dispatch: Dispatch) => {
  dispatch({ type: SAVE_USER_REQUEST });
  let url = `${Config.API_SERVICE_URL}/api/users/saveUser`;
  return axios
    .post(url,userDetails, getHeaders())
    .then((response) => {
      ToastService.getInstance().publishToast(
        {
          identifier: "success-operation",
          title: "Success",
          message: "User created successfully!",
        },
        SeverityLevel.SUCCESS,
        { autoClose: 3000 }
      );
      dispatch({ type: SAVE_USER, payload: response.data });
    })
    .catch((e) => {
      dispatch({ type: SAVE_USER_FAILED ,payload:e.response.status});
    });
}

const patchUser: ThunkActionCreator<Promise<void>> = (userDetails,id) => (dispatch: Dispatch) => {
  dispatch({ type: PATCH_USER_REQUEST });
  let url = `${Config.API_SERVICE_URL}/api/users/patchUser/${id}`;
  return axios
    .post(url,userDetails, getHeaders())
    .then((response) => {
      ToastService.getInstance().publishToast(
        {
          identifier: "success-operation",
          title: "Success",
          message: "User updated successfully!",
        },
        SeverityLevel.SUCCESS,
        { autoClose: 3000 }
      );
      dispatch({ type: PATCH_USER, payload: response.data });
    })
    .catch((e) => {
      dispatch({ type: PATCH_USER_FAILED ,payload:e.response.status});
    });
}

const exportUsersCsv: ThunkActionCreator<Promise<void>> =
  (
    queryParams?: {
      email?: string;
      firstname?: string;
      lastname?: string;
      role?: string;
      company?: string;
      lastLoggedIn?: string;
      createdAt?: string;
      sortField?: string;
      sortOrder?: SORTING_ORDER;
    },
    onProgress?: (progress: { percent: number; exported: number; total: number }) => void
  ) =>
  async () => {
    const now = new Date();
    const pad = (value: number) => String(value).padStart(2, "0");
    const currentDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const currentTime = `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
    const exportFilename = `users_${currentDate}_${currentTime}.csv`;

    const params: Record<string, any> = {};
    if (queryParams?.email) params.email = queryParams.email;
    if (queryParams?.firstname) params.firstname = queryParams.firstname;
    if (queryParams?.lastname) params.lastname = queryParams.lastname;
    if (queryParams?.role) params.role = queryParams.role;
    if (queryParams?.company) params.company = queryParams.company;
    if (queryParams?.lastLoggedIn) params.lastLoggedIn = queryParams.lastLoggedIn;
    if (queryParams?.createdAt) params.createdAt = queryParams.createdAt;
    if (queryParams?.sortField) {
      params.sortField = queryParams.sortField;
      params.sortOrder = queryParams.sortOrder || SORTING_ORDER.DESC;
    }

    try {
      const countResponse = await axios.get(
        `${Config.API_SERVICE_URL}/api/users?${queryString.stringify({ ...params, page: 1, limit: 100 })}`,
        getHeaders()
      );
      const totalRows = Number(countResponse.data?.pagination?.total || 0);
      onProgress?.({ percent: 0, exported: 0, total: totalRows });

      const response = await axios.get(
        `${Config.API_SERVICE_URL}/api/users/export?${queryString.stringify(params)}`,
        {
          ...getHeaders(),
          responseType: "blob",
          onDownloadProgress: (event: AxiosProgressEvent) => {
            if (!totalRows) {
              return;
            }

            const totalBytes = event.total ?? 0;
            let percent = 0;

            if (totalBytes > 0) {
              const rawPercent = Math.round((event.loaded / totalBytes) * 100);
              percent = Math.max(0, Math.min(99, rawPercent));
            } else {
              const loadedBytes = event.loaded ?? 0;
              const estimated = Math.round((1 - Math.exp(-loadedBytes / 350000)) * 100);
              percent = Math.max(1, Math.min(95, estimated));
            }

            const exported = Math.min(totalRows, Math.round((percent / 100) * totalRows));
            onProgress?.({ percent, exported, total: totalRows });
          },
        }
      );

      const contentTypeHeader = response.headers?.["content-type"];
      const contentType = (typeof contentTypeHeader === "string" ? contentTypeHeader : "").toLowerCase();
      const isCsvResponse =
        contentType.includes("text/csv") ||
        contentType.includes("application/csv") ||
        contentType.includes("application/octet-stream");

      if (!isCsvResponse) {
        throw new Error(`Unexpected export content type: ${contentType || "unknown"}`);
      }

      onProgress?.({ percent: 100, exported: totalRows, total: totalRows });

      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = exportFilename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
    } catch (e) {
      ToastService.getInstance().publishToast(
        {
          identifier: "users-export-failed",
          title: "Export failed",
          message: "Unable to export users CSV. Please try again.",
        },
        SeverityLevel.ERROR,
        { autoClose: 3000 }
      );
      throw e;
    }
  };


// Actions
const actions = {
  [CREATE_PROJECT_REQUEST]: () => ({ ...initialState }),
  [DELETE_USER]: (state: UsersState, action: AnyAction): UsersState => ({
    ...state,
    content: {
      data: (state.content?.data || []).filter((u: User) => u.id !== action.payload),
    },
  }),
  [FETCH_USERS_BY_PROJECT_REQUEST]: (state: UsersState) => ({
    ...state,
    loading: true,
  }),
  [FETCH_USERS_BY_PROJECT]: (state: UsersState, action: AnyAction): UsersState => ({
    ...state,
    loading: false,
    content: action.payload,
  }),
  [FETCH_USERS_BY_PROJECT_FAILED]: (state: UsersState, action: AnyAction): UsersState => ({
    ...state,
    loading: false,
    error: true,
    content: initialState.content,
  }),
  [FETCH_USERS_REQUEST]: (state: UsersState) => ({
    ...state,
    loading: true,
  }),
  [FETCH_USERS]: (state: UsersState, action: AnyAction): UsersState => ({
    ...state,
    loading: false,
    content: { data: action.payload.data },
    pagination: action.payload.pagination,
  }),
  [FETCH_USERS_FAILED]: (state: UsersState, action: AnyAction): UsersState => ({
    ...state,
    loading: false,
    error: true,
    content: initialState.content,
  }),
  [FILTER_USERS_REQUEST]: (state: UsersState) => ({
    ...state,
    loading: true,
  }),
  [FILTER_USERS]: (state: UsersState, action: AnyAction): UsersState => ({
    ...state,
    loading: false,
    content: { data: action.payload.data },
    pagination: action.payload.pagination,
  }),
  [FILTER_USERS_FAILED]: (state: UsersState, action: AnyAction): UsersState => ({
    ...state,
    loading: false,
    error: true,
    content: initialState.content,
  }),
  [CHECK_USERS_REQUEST]: (state: UsersState) => ({
    ...state,
    loading: true,
  }),
  [CHECK_USERS]: (state: UsersState, action: AnyAction): UsersState => ({
    ...state,
    loading: false,
    checkUsers: action.payload,
  }),
  [CHECK_USERS_FAILED]: (state: UsersState, action: AnyAction): UsersState => ({
    ...state,
    loading: false,
    error: true,
    checkUsers: initialState.content,
  }),

  [FETCH_REGIONS_REQUEST]: (state: UsersState) => ({
    ...state,
    loading: true,
  }),
  [FETCH_REGIONS]: (state: UsersState, action: AnyAction): UsersState => ({
    ...state,
    loading: false,
    regions: action.payload,
  }),
  [FETCH_REGIONS_FAILED]: (state: UsersState, action: AnyAction): UsersState => ({
    ...state,
    loading: false,
    error: true,
    regions: [],
  }),

  [SAVE_USER_REQUEST]: (state: UsersState) => ({
    ...state,
    loading: true,
  }),
  [SAVE_USER]: (state: UsersState, action: AnyAction): UsersState => ({
    ...state,
    loading: false,
    savedUser: action.payload,
    errorMessage:0
  }),
  [SAVE_USER_FAILED]: (state: UsersState, action: AnyAction): UsersState => ({
    ...state,
    loading: false,
    error: true,
    savedUser: [],
    errorMessage:action.payload
  }),
  [PATCH_USER_REQUEST]: (state: UsersState) => ({
    ...state,
    loading: true,
  }),
  [PATCH_USER]: (state: UsersState, action: AnyAction): UsersState => ({
    ...state,
    loading: false,
    savedUser: action.payload,
    errorMessage:0
  }),
  [PATCH_USER_FAILED]: (state: UsersState, action: AnyAction): UsersState => ({
    ...state,
    loading: false,
    error: true,
    savedUser: [],
    errorMessage:action.payload
  })
};

// Reducer Interface
export interface UsersPagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface UsersState {
  error?: boolean;
  loading?: boolean;
  content?: PaginatedResponse<User>;
  pagination?: UsersPagination;
  checkUsers?: any;
  regions:[];
  savedUser:any;
  errorMessage:number;
}

// Initial State
const initialState: UsersState = {
  loading: false,
  error: false,
  content: { data: [] },
  pagination: { total: 0, page: 1, limit: 100, totalPages: 0 },
  checkUsers: undefined,
  regions: [],
  savedUser: undefined,
  errorMessage: 0,
};

export { fetchUsers, fetchUsersByProjectId, filterUsers,getRegions,saveUser,patchUser, exportUsersCsv, initialState,checkUsers };

// Reducer
export default handleActions<UsersState, AnyAction>(actions, initialState);
